import { createContext, type ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";

/**
 * The height the header is assumed to claim before it has been measured, in px.
 *
 * This was once the whole story — a constant both engines subtracted from the
 * measured parent height. `TimeControls` ended that: it is `w-full flex-wrap`,
 * so a header hosting it is two rows at narrow widths and one row at wide ones.
 * No constant describes that, and a constant that disagrees with the render is
 * exactly the drift this component exists to remove.
 *
 * It survives as the first-paint estimate. Without it the header reserves zero
 * until the observer fires and every chart on the page visibly reflows.
 */
export const CHART_HEADER_HEIGHT = 48;

/**
 * Does an ancestor already put a visible label on this chart?
 *
 * Charts mostly live inside a titled container — a collapse section, a card —
 * whose title is not decoration but the affordance you click to expand, and the
 * only thing on screen while the section is closed. Those titles cannot be
 * removed, so a chart that always renders its own title duplicates them.
 *
 * Rather than a per-call-site flag someone has to remember (the drift this
 * component exists to stop), titled containers declare themselves once with
 * `ChartLabelledBy` and every chart beneath them suppresses its own title.
 *
 * Defaults to `false` — assume nothing labels the chart, so a chart standing on
 * its own says what it is. The containers that do label their charts declare it:
 * `Section` when it has a title, `Card` when its header has one, and
 * `CollapseContent` always, since a disclosure's header is its affordance. An
 * untitled container stays transparent rather than re-granting the title, so
 * nesting composes.
 *
 * Three surfaces label their charts with markup of their own and so wrap
 * explicitly: `TelemetryChartsView` (an uppercase title above each panel, beside
 * the `ChartExpandOwnedByAncestor` it already declares), `DeviceTelemetrySparklines`
 * (a label above each 60px sparkline), and `MonitorEditor/PreviewPanel` (a
 * chrome-less 130px preview in an already-titled card).
 */
const ChartLabelledContext = createContext(false);

/**
 * The header's measured width, published to whatever is rendered in `controls`.
 *
 * A control cluster that wants to collapse when space runs out cannot measure
 * its own slot to decide: in the wide layout that slot is sized by its content,
 * so a cluster that has already collapsed reports the width of its own collapsed
 * trigger and can never expand again. The header is the stable frame — it is
 * sized by the layout, not by what it contains — so it is the one that measures.
 *
 * `null` means "not measured yet". Consumers should treat that as the ROOMY
 * case: a first paint that briefly shows the full cluster settling into a
 * collapsed one is a reflow, while the reverse hides controls that are about to
 * be available.
 */
const ChartHeaderWidthContext = createContext<number | null>(null);

/**
 * Width of the enclosing `ChartHeader`, or `null` outside one / before it is
 * measured. See {@link ChartHeaderWidthContext}.
 */
export function useChartHeaderWidth(): number | null {
  return useContext(ChartHeaderWidthContext);
}

/**
 * Marks a subtree as already carrying a visible label, so charts inside it do
 * not render their own title. Wrap the body of a titled section or card.
 */
export function ChartLabelledBy({ children }: { children: ReactNode }) {
  return <ChartLabelledContext.Provider value={true}>{children}</ChartLabelledContext.Provider>;
}

/**
 * Marks a subtree as unlabelled, so charts inside it own their title. Use where
 * a chart stands on its own with no section or card heading above it.
 */
export function ChartOwnsItsTitle({ children }: { children: ReactNode }) {
  return <ChartLabelledContext.Provider value={false}>{children}</ChartLabelledContext.Provider>;
}

/**
 * The height the header claims here, and the ref that establishes it.
 *
 * Both engines subtract `headerHeight` from the measured parent height before
 * sizing the plot, and pass `headerRef` to the `ChartHeader` they render. The
 * reserve is therefore the render, measured — not a second statement of it that
 * someone has to keep in agreement.
 *
 * Returns 0 when the header will render nothing, so a chart that is already
 * labelled and has no slots filled loses no plot height to empty chrome.
 */
export function useChartHeaderMetrics(
  hasActions: boolean,
  hasControls: boolean
): { headerRef: (node: HTMLDivElement | null) => void; headerHeight: number } {
  const labelled = useContext(ChartLabelledContext);
  const { ref, height } = useOccupiedHeight();
  const renders = !labelled || hasActions || hasControls;
  return { headerRef: ref, headerHeight: renders ? height || CHART_HEADER_HEIGHT : 0 };
}

/** Returns `true` when the chart should render its own visible title. */
export function useChartOwnsItsTitle(): boolean {
  return !useContext(ChartLabelledContext);
}

/**
 * Height the element actually occupies, padding included.
 *
 * Deliberately not `useElementSize`: that hook reports `ResizeObserverEntry.contentRect`,
 * which excludes padding, so the header's own bottom gap would fall outside the
 * number both engines subtract and every chart would under-reserve by exactly
 * that gap (Codex on #14497). `offsetHeight` is the border box, which is what
 * "how much room does this take" means here. The observer only says *when* to
 * re-read.
 */
export function useOccupiedHeight(): { ref: (node: HTMLDivElement | null) => void; height: number } {
  const [height, setHeight] = useState(0);
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  const ref = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    nodeRef.current = node;

    if (!node) {
      setHeight(0);
      return;
    }

    const read = () => setHeight(nodeRef.current?.offsetHeight ?? 0);
    read();
    observerRef.current = new ResizeObserver(read);
    observerRef.current.observe(node);
  }, []);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return { ref, height };
}

export interface ChartHeaderProps {
  /**
   * The chart's title. Both engines pass the same string they render as the SVG
   * `<title>`, so the accessible name and the visible heading cannot drift
   * apart (ALL-2539). Shown only when nothing above already labels the chart.
   */
  title: string;
  /**
   * Control cluster — `TimeControls` today.
   *
   * Separate from `actions` because it is not a trailing icon: it is full-width
   * and it wraps, which is the whole reason this header measures itself rather
   * than reserving a constant.
   *
   * Belongs here when it governs exactly this chart. Controls that govern a
   * composed panel stay at panel level: `SiteEnergyCharts` pairs them with a KPI
   * strip and expands the whole body as one unit, `Dnp3TelemetryChartsSection`
   * drives a grid of charts from one cluster, and `TiledTimeSeriesExplorer`
   * swaps its chart for an `EmptyState` — moving those controls inside the chart
   * would reorder them, or make them vanish exactly when the window needs
   * changing.
   */
  controls?: ReactNode;
  /** Trailing action slot — expand first, more later. */
  actions?: ReactNode;
  /** Measurement ref from `useChartHeaderMetrics`. */
  containerRef?: (node: HTMLDivElement | null) => void;
  /**
   * Horizontal insets, in px. Both default to 0 and no engine passes them.
   *
   * They exist for a caller that genuinely needs the header indented, NOT for
   * aligning with the plot. Both engines used to pass the plot's own
   * `dynamicMargin.right` here, which put the expand action 8px in from the card
   * edge while the title stayed flush left — a lopsided header, visible on every
   * chart in the app. That margin is axis padding, so a point at the domain max
   * does not sit on the plot border; it describes the PLOT, and the header is
   * chrome that shares the card's edges with everything else in it.
   */
  marginLeft?: number;
  marginRight?: number;
}

/**
 * `ChartHeader` — top chrome for both chart engines, symmetric with
 * `ChartBottomBar`.
 *
 * `ChartBottomBar` is rendered by `ChartContainer` and `TiledTimeSeriesChart`
 * alike, and hands every consumer legend, series toggling, export and reset
 * zoom for free. Above the plot there was nothing, so title, time controls and
 * expand were written per chart in app code — and drifted, because the drift
 * stops exactly at the boundary of the shared component. This is that boundary,
 * moved up.
 *
 * Deliberately has no `hideHeader` prop. `hideBottomBar` is how a shared feature
 * becomes optional and then absent.
 */
/**
 * The header's two layouts, chosen by the header's OWN width.
 *
 * A chart header is not always the width of the window. Dropped into a
 * two-column entity page the same header is ~490px on a 1512px screen, so a
 * viewport breakpoint reads "wide" and lays out a row with nowhere to go. The
 * container query measures the element that actually has to fit the controls.
 *
 * WIDE (>= 720px): title on the left, controls trailing on the same line,
 * actions pinned right. One line, the dense layout.
 *
 * NARROW (< 720px): the controls drop to their OWN row beneath the title and
 * stretch full width, each select growing to share the space. This is the case
 * the previous flex-wrap-on-one-row version got wrong: the controls slot was
 * flex-1, so it grew to claim the whole line and overlapped the shrink-0 title,
 * while each control wrapped separately into a staircase.
 *
 * Written as a scoped stylesheet rather than Tailwind utilities because
 * container queries need a named container and a variant Tailwind does not emit
 * here: these packages ship no CSS pipeline of their own, so utilities are
 * whatever the consuming app happens to have generated.
 */
const HEADER_STYLES = `
/*
 * The container is a WRAPPER, not the grid itself. A container query cannot
 * style its own container, so when .chart-header carried container-type the
 * query's own grid-template-columns: auto 1fr auto never applied — the base
 * 1fr auto stayed, and the third track was an IMPLICIT one created by the
 * actions' grid-column: 3. The wide layout worked by accident and the
 * controls never got the flexible centre column they were written for.
 */
.chart-header-container {
  container-type: inline-size;
  container-name: chart-header;
}
.chart-header {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  column-gap: 0.75rem;
  min-height: 32px;
}
.chart-header__title {
  grid-column: 1;
  grid-row: 1;
  min-width: 0;
}
.chart-header__actions {
  grid-column: 2;
  grid-row: 1;
  /*
   * Pulled out by the icon button's own internal padding so the GLYPH lands on
   * the card's content edge, not the button's invisible box.
   *
   * An icon Button is a 44px ghost target with 12px of padding, so a button
   * flush to the edge draws its arrows 12px inside it. Everything it lines up
   * against is bordered — Export in the bottom bar, the controls beside it —
   * where the box edge IS the visual edge. Matching boxes therefore looked
   * wrong, which is the thing being corrected: this aligns what the eye
   * actually sees.
   *
   * Expressed as the same token the button pads itself with, so the two cannot
   * drift: a change to the control scale moves the padding and this pull
   * together. The fallback matches the token's current value.
   */
  margin-right: calc(-1 * var(--control-padding-sm, 0.75rem));
}
.chart-header__controls {
  grid-column: 1 / -1;
  grid-row: 2;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
}
.chart-header__controls > * {
  flex: 1 1 auto;
  min-width: 0;
}
/*
 * NARROW with NO VISIBLE TITLE: the controls take row 1, beside the actions.
 *
 * The narrow layout drops the controls to their own row because a title and a
 * full-width control cluster cannot share a line. With nothing in the title
 * slot that reason is gone — and the cost was visible: a chart inside a titled
 * container (ChartLabelledBy, which every RouteSection and Card sets)
 * rendered an empty title span holding row 1, so the expand action sat alone on
 * one row and the controls on another. Two rows of chrome, one of them blank,
 * above a chart in the surface that can least afford the height.
 *
 * Keyed off data-chart-header-untitled, set from the same showTitle that
 * decides what the title slot renders, rather than off :empty or a sibling
 * selector — the flag states the intent once and cannot disagree with the
 * markup it describes.
 *
 * The cluster still gets the full remaining width and still collapses on its
 * own terms (StateHistoryControlBar reads the header width and folds behind a
 * single trigger below 720px), so this moves WHERE the collapsed trigger sits,
 * not whether it collapses.
 */
.chart-header[data-chart-header-untitled] .chart-header__controls {
  grid-column: 1;
  grid-row: 1;
  margin-top: 0;
  flex-wrap: nowrap;
  min-width: 0;
}
/*
 * The placeholder span holds the wide layout's first track (the grid needs
 * something in column 1 for auto/1fr/auto to mean what it says), so it stays
 * in the markup — but it must not also occupy the cell the controls just took,
 * or the two stack in the same grid area. Narrow and untitled, it is removed
 * from flow; the wide query below puts it back.
 */
.chart-header[data-chart-header-untitled] .chart-header__title {
  display: none;
}
@container chart-header (width >= 720px) {
  .chart-header {
    grid-template-columns: auto 1fr auto;
  }
  .chart-header__controls {
    grid-column: 2;
    grid-row: 1;
    margin-top: 0;
    /*
     * space-between, not flex-end. A cluster that groups its controls (the
     * state-history bar puts "what is charted" in one group and "over what
     * window" in another) gets its first group left-aligned against the title
     * and the rest pushed right, instead of one undifferentiated run of five
     * controls where the metric reads like the range. A cluster with a single
     * child is unaffected — there is nothing to space apart, and margin-left:auto on that child keeps it right-aligned.
     */
    justify-content: space-between;
    flex-wrap: nowrap;
  }
  .chart-header__controls > :only-child {
    margin-left: auto;
  }
  .chart-header__controls > * {
    flex: 0 0 auto;
  }
  .chart-header__actions {
    grid-column: 3;
  }
  /*
   * Wide: the placeholder returns to column 1 so auto/1fr/auto keeps three
   * tracks and the controls keep the flexible centre one. Hiding it here would
   * collapse the grid to two tracks and drag the cluster left, under the title
   * position — the wide layout is unchanged by this fix.
   */
  .chart-header[data-chart-header-untitled] .chart-header__title {
    display: block;
  }
  .chart-header[data-chart-header-untitled] .chart-header__controls {
    grid-column: 2;
  }
}
`;

export function ChartHeader({
  title,
  controls,
  actions,
  containerRef,
  marginLeft = 0,
  marginRight = 0,
}: ChartHeaderProps) {
  const labelled = useContext(ChartLabelledContext);
  const showTitle = !labelled;

  /*
   * The header measures itself so `controls` can decide whether it fits. The
   * chart engine already owns `containerRef` (it subtracts the header height
   * from the plot), so this composes with it rather than replacing it.
   */
  const [width, setWidth] = useState<number | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const measureRef = useCallback(
    (node: HTMLDivElement | null) => {
      containerRef?.(node);
      observerRef.current?.disconnect();
      observerRef.current = null;
      if (!node) return;
      const read = () => setWidth(node.offsetWidth);
      read();
      observerRef.current = new ResizeObserver(read);
      observerRef.current.observe(node);
    },
    [containerRef]
  );

  useEffect(() => () => observerRef.current?.disconnect(), []);

  if (!showTitle && !controls && !actions) return null;

  return (
    <ChartHeaderWidthContext.Provider value={width}>
      <style>{HEADER_STYLES}</style>
      {/*
        The wrapper exists ONLY to be the query container — see HEADER_STYLES.
        Padding and margins stay on the inner element so the measured height
        (which both engines subtract from the plot) still includes them.
      */}
      <div className="chart-header-container shrink-0" style={{ marginLeft, marginRight }}>
        <div
          ref={measureRef}
          data-chart-header=""
          {...(showTitle ? {} : { "data-chart-header-untitled": "" })}
          className="chart-header pb-4"
        >
          {showTitle ? (
            <h3 className="chart-header__title truncate text-sm font-medium text-text-primary">{title}</h3>
          ) : (
            <span className="chart-header__title" />
          )}
          {controls ? (
            <div data-chart-header-controls="" className="chart-header__controls">
              {controls}
            </div>
          ) : null}
          {actions ? (
            <div data-chart-header-actions="" className="chart-header__actions flex shrink-0 items-center gap-1">
              {actions}
            </div>
          ) : null}
        </div>
      </div>
    </ChartHeaderWidthContext.Provider>
  );
}
